import * as React from "react";
import InputText from "./InputText";
import styles from "./AddressAutocomplete.module.scss";

enum Status {
    Empty,
    FetchingNew,
    DisplayingResults
}

export type PageAutocompleteState = {
    address_autocomplete_manual_entry: boolean;
}

export type AddressAutocompleteProps = {
    fill_result_fields: (item: AddressResult) => void;
    switch_manual_entry: (value: boolean) => void;
};

type AddressAutocompleteState = {
    address_autocomplete_search: string;
    address_autocomplete_status: Status;
    address_autocomplete_results: ReturnItem[];
};

export type AddressResult = {
    Line1: string;
    Line2?: string;
    Line3?: string;
    Town: string;
    County: string;
    Postcode: string;
};

export type ReturnItem = {
    label: string;
    is_actionable: boolean;
    result?: AddressResult;
};

export class AddressAutocomplete extends React.Component<AddressAutocompleteProps, AddressAutocompleteState> {
    private abort: AbortController | null = null;

    constructor(props: AddressAutocompleteProps) {
        super(props);
        this.state = {
            address_autocomplete_search: "",
            address_autocomplete_status: Status.Empty,
            address_autocomplete_results: []
        }
    }

    private send_result(item: AddressResult) {
        this.props.fill_result_fields(item);
        this.props.switch_manual_entry(false);
        this.setState(() => {
            return { address_autocomplete_search: "", address_autocomplete_status: Status.Empty, address_autocomplete_results: [] };
        });
    }

    private send_manual_entry_status(value: boolean) {
        this.setState(() => {
            return { address_autocomplete_status: Status.Empty, address_autocomplete_results: [] };
        })
        this.props.switch_manual_entry(value);
    }

    private update_search(_: string, value?: string) {
        this.setState(() => {
            return { address_autocomplete_search: value ?? "", address_autocomplete_status: Status.FetchingNew };
        })

        this.fetch_results('/uk/cleaning-jobs/address-autocomplete?address={0}', value)
            .then(async (result) => {
                if (!result) {
                    this.setState(() => {
                        return { address_autocomplete_status: Status.Empty, address_autocomplete_results: [] };
                    });
                    return;
                }

                this.setState(() => {
                    return { address_autocomplete_status: Status.DisplayingResults, address_autocomplete_results: result };
                });
            });
    }

    private async fetch_results(url: string, address?: string) : Promise<ReturnItem[] | null> {
        if (!address || address.length < 3) {
            return null;
        }

        try {
            if (this.abort !== null) {
                this.abort.abort();
            }

            this.abort = new AbortController();
            const response = await fetch(url.replace("{0}", encodeURIComponent(address)), {
                signal: this.abort?.signal
            });
            const result = await response.json() as AddressResult[];

            const resultOptions = result.map(e => { return { label: this.get_display_label_for_result(e), is_actionable: true, result: e } as ReturnItem });
            return [ { label: "Click here to enter an address manually.", is_actionable: false }, ...resultOptions ];
        }
        catch {
            if (this.state.address_autocomplete_status === Status.FetchingNew) {
                return [{ label: "Loading...", is_actionable: false }]
            }
            return [ { label: "There was an error. Click here to enter an address manually.", is_actionable: false } ]
        }
    }

    private get_display_label_for_result(item: AddressResult) {
        let display = item.Line1;
        if (item.Line2)
        {
            display += ", ";
            display += item.Line2;
        }
        if (item.Line3)
        {
            display += ", ";
            display += item.Line3;
        }
        display += ", ";
        display += item.Town;

        return display;
    }

    render() {
        return <>
            <InputText
                label="Please enter the first line of your address"
                name="address_search"
                autoComplete="address-line1"
                value={this.state.address_autocomplete_search}
                required={false}
                validation_triggered={false}
                validation_get_scrolled={() => true}
                validation_set_scrolled={() => { }}
                update_value={(name, value) => this.update_search(name, value)}
            />
            {
                this.state.address_autocomplete_status === Status.DisplayingResults && this.state.address_autocomplete_results.length > 0
                    ? <div className={`form-autocomplete open ${styles.wrapper}`}>
                        <ul className="dropdown-menu dropdown-autocomplete">
                            {this.state.address_autocomplete_results.map((item, idx) => {
                                return <li key={`main-address-ac-${idx}`}><a href="#" className="dropdown-item" onClick={(ev) => {
                                    ev.preventDefault();
                                    if (item.is_actionable && item.result) {
                                        this.send_result(item.result);
                                    }
                                    else {
                                        this.send_manual_entry_status(true);
                                    }
                                }}>{item.is_actionable ? <>{item.label}</> : <strong>{item.label}</strong>}</a></li>
                            })}
                        </ul>
                    </div>
                    : null
            }
        </>
    }
}